import * as React from 'react';
import { jwtDecode } from 'jwt-decode';
import i18n from '../../shared/i18n';
import { SystemRole } from '../../helper-types/general/enums/system-role';

interface User {
    id: number;
    name: string;
    email: string;
    image: string;
    isAdmin: boolean;
    isStudent: boolean;
    // True when this session belongs to a platform admin who has "entered" this tenant
    // (see PlatformAdminService.SelectTenantAsync) rather than a real tenant User account —
    // `id` above is the PlatformAdmin's own id, not a Users.Id, so anything that looks up
    // "my own" User row by id (like the profile page) must not run for this session. This
    // is distinct from the backend's own "IsPlatformAdmin" claim, which means full
    // cross-tenant bypass mode and is never true while browsing a single entered tenant.
    isPlatformAdminImpersonation: boolean;
}

interface AuthContextType {
    isAuthenticated: boolean;
    userRoles: string[];
    user: User | null;
    tenantSubdomain: string | null;
    // Resolved from tenantNameAr/tenantNameEn against the current UI language — stays a single
    // string so every consumer (layout, dashboard header, watermark, etc.) keeps reading one
    // field regardless of which language is active.
    tenantName: string | null;
    tenantLogoUrl: string | null;
    tenantPrimaryColor: string | null;
    tenantSecondaryColor: string | null;
    isLoading: boolean;
    login: (
        token: string,
        tenantNameAr?: string,
        tenantNameEn?: string,
        tenantLogoUrl?: string | null,
        tenantSubdomain?: string,
        tenantPrimaryColor?: string | null,
        tenantSecondaryColor?: string | null,
    ) => void;
    logout: () => void;
}

// Shape of the claims the backend's JwtBearerService puts in the token — ASP.NET's default
// claim types are long URIs rather than plain names, see JwtBearerService.GenerateToken.
interface DecodedJwtClaims {
    sid?: string;
    ImpersonatedByPlatformAdmin?: string;
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'?: string | string[];
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'?: string;
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'?: string;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [userRoles, setUserRoles] = React.useState<string[]>([]);
    const [user, setUser] = React.useState<User | null>(null);
    const [tenantSubdomain, setTenantSubdomain] = React.useState<string | null>(null);
    const [tenantNameAr, setTenantNameAr] = React.useState<string | null>(null);
    const [tenantNameEn, setTenantNameEn] = React.useState<string | null>(null);
    const [tenantLogoUrl, setTenantLogoUrl] = React.useState<string | null>(null);
    const [tenantPrimaryColor, setTenantPrimaryColor] = React.useState<string | null>(null);
    const [tenantSecondaryColor, setTenantSecondaryColor] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    // i18n.language isn't itself reactive React state, so a plain language-switch elsewhere in
    // the app wouldn't otherwise re-render this provider (and re-resolve tenantName below).
    const [language, setLanguage] = React.useState(i18n.language);

    React.useEffect(() => {
        const handleLanguageChanged = (lng: string) => setLanguage(lng);
        i18n.on('languageChanged', handleLanguageChanged);
        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, []);

    const tenantName = React.useMemo(() => {
        const resolved = language.startsWith('ar') ? tenantNameAr : tenantNameEn;
        return resolved ?? tenantNameAr ?? tenantNameEn ?? null;
    }, [language, tenantNameAr, tenantNameEn]);

    const login = (
        token: string,
        tenantNameArParam?: string,
        tenantNameEnParam?: string,
        tenantLogoUrlParam?: string | null,
        tenantSubdomainParam?: string,
        tenantPrimaryColorParam?: string | null,
        tenantSecondaryColorParam?: string | null,
    ) => {
        localStorage.setItem('bearerToken', token);
        if (tenantNameArParam) {
            localStorage.setItem('tenantNameAr', tenantNameArParam);
            setTenantNameAr(tenantNameArParam);
        }
        if (tenantNameEnParam) {
            localStorage.setItem('tenantNameEn', tenantNameEnParam);
            setTenantNameEn(tenantNameEnParam);
        }
        if (tenantLogoUrlParam !== undefined) {
            if (tenantLogoUrlParam) {
                localStorage.setItem('tenantLogoUrl', tenantLogoUrlParam);
            } else {
                localStorage.removeItem('tenantLogoUrl');
            }
            setTenantLogoUrl(tenantLogoUrlParam);
        }
        if (tenantSubdomainParam) {
            localStorage.setItem('tenantSubdomain', tenantSubdomainParam);
            setTenantSubdomain(tenantSubdomainParam);
        }
        if (tenantPrimaryColorParam) {
            localStorage.setItem('tenantPrimaryColor', tenantPrimaryColorParam);
            setTenantPrimaryColor(tenantPrimaryColorParam);
        }
        if (tenantSecondaryColorParam) {
            localStorage.setItem('tenantSecondaryColor', tenantSecondaryColorParam);
            setTenantSecondaryColor(tenantSecondaryColorParam);
        }

        try {
            const decoded = jwtDecode<DecodedJwtClaims>(token);

            const roleClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
            const roles = roleClaim
                ? Array.isArray(roleClaim)
                    ? roleClaim
                    : [roleClaim]
                : [];

            const userData: User = {
                id: Number(decoded['sid']) || 0,
                name: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '',
                email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '',
                image: '',
                isAdmin: roles.includes(SystemRole.Administrator),
                isStudent: roles.includes(SystemRole.Student),
                // Backend issues this as the literal claim name "ImpersonatedByPlatformAdmin"
                // with a stringified bool value ("True"/"False") — see
                // MiscConstants.ImpersonatedByPlatformAdmin.
                isPlatformAdminImpersonation: decoded['ImpersonatedByPlatformAdmin'] === 'True',
            };

            setUserRoles(roles);
            setUser(userData);
            setIsAuthenticated(true);
        } catch (error) {
            setUserRoles([]);
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('bearerToken');
        localStorage.removeItem('tenantNameAr');
        localStorage.removeItem('tenantNameEn');
        localStorage.removeItem('tenantLogoUrl');
        localStorage.removeItem('tenantSubdomain');
        localStorage.removeItem('tenantPrimaryColor');
        localStorage.removeItem('tenantSecondaryColor');
        document.title = i18n.t('dashboard.systemName');
        setIsAuthenticated(false);
        setUserRoles([]);
        setUser(null);
        setTenantSubdomain(null);
        setTenantNameAr(null);
        setTenantNameEn(null);
        setTenantLogoUrl(null);
        setTenantPrimaryColor(null);
        setTenantSecondaryColor(null);
    };

    React.useEffect(() => {
        const token = localStorage.getItem('bearerToken');
        const savedTenantNameAr = localStorage.getItem('tenantNameAr');
        const savedTenantNameEn = localStorage.getItem('tenantNameEn');
        const savedTenantLogoUrl = localStorage.getItem('tenantLogoUrl');
        if (savedTenantNameAr) {
            setTenantNameAr(savedTenantNameAr);
        }
        if (savedTenantNameEn) {
            setTenantNameEn(savedTenantNameEn);
        }
        if (savedTenantLogoUrl) {
            setTenantLogoUrl(savedTenantLogoUrl);
        }
        const savedTenantSubdomain = localStorage.getItem('tenantSubdomain');
        if (savedTenantSubdomain) {
            setTenantSubdomain(savedTenantSubdomain);
        }
        const savedTenantPrimaryColor = localStorage.getItem('tenantPrimaryColor');
        if (savedTenantPrimaryColor) {
            setTenantPrimaryColor(savedTenantPrimaryColor);
        }
        const savedTenantSecondaryColor = localStorage.getItem('tenantSecondaryColor');
        if (savedTenantSecondaryColor) {
            setTenantSecondaryColor(savedTenantSecondaryColor);
        }
        if (token) {
            try {
                const decoded = jwtDecode<{ exp?: number }>(token);

                if (decoded.exp && decoded.exp * 1000 < Date.now()) {
                    logout();
                } else {
                    login(token);
                }
            } catch (error) {
                logout();
            }
        }
        setIsLoading(false);
    }, []);

    // Update page title based on tenant name
    React.useEffect(() => {
        document.title = tenantName || i18n.t('dashboard.systemName');
    }, [tenantName]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, userRoles, user, tenantSubdomain, tenantName, tenantLogoUrl, tenantPrimaryColor, tenantSecondaryColor, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};